import type {
  ParamDownloadStatus,
  ParamMetadataSourceStatus,
  StatustextEntry,
  VehicleClass,
} from '@hgc/core';
import type { StatusLevel } from '@hgc/ui';

import type { SetupTab } from '../tabs';

/**
 * 机体摘要的**事实推导**。
 *
 * 这里是纯函数，没有 React、没有运行时依赖，理由很实际：摘要页最容易变成
 * "一堆看起来专业的绿勾"。把每条结论压到"要么是一个真实读数，要么如实说不知道"，
 * 就必须能单独测它 —— 真机上的传感器故障没法在界面上复现。
 *
 * 三条规则：
 *   1. **不知道就说不知道。** 没有数据 = `state: 'unknown'`，不是"正常"。
 *   2. **结论必须能追到来源。** 每条 `detail` 都写清是从哪个读数/哪个参数得出的。
 *   3. **"还没配好"只列真的能判断的项。** 判断不了的（例如"传感器是否已校准"）如实
 *      说"无法从遥测判断"，而不是猜一个"正常"。
 */

export interface HealthItem {
  readonly id: string;
  readonly label: string;
  readonly level: StatusLevel;
  readonly value: string;
  readonly detail: string;
  /** `false` = 没有数据，不能当成正常 */
  readonly known: boolean;
}

export interface TodoItem {
  readonly id: string;
  readonly label: string;
  readonly level: StatusLevel;
  readonly detail: string;
  readonly target: SetupTab | null;
  readonly targetLabel: string | null;
}

export interface VehicleFactsInput {
  readonly vehicleClass: VehicleClass;
  readonly firmware: FirmwareInfo;
  readonly paramCount: number;
  readonly paramExpected: number | null;
  readonly downloadStatus: ParamDownloadStatus | null;
  readonly missingParams: number;
  readonly metadataStatus: ParamMetadataSourceStatus | null;
  readonly metadataSize: number;
  /** 从参数表里读到的值（还没读到参数表时为 null） */
  readonly frameClass: number | null;
  readonly frameType: number | null;
  readonly rtlAltCm: number | null;
  readonly ekfHealthy: boolean | null;
  readonly fixType: number | null;
  readonly fixTypeName: string | null;
  readonly satellites: number | null;
  readonly hdop: number | null;
  readonly voltageV: number | null;
  readonly remainingPct: number | null;
  readonly vibrationClipping: number | null;
  readonly rcChannels: readonly (number | null)[] | null;
}

export interface VehicleFacts {
  readonly health: readonly HealthItem[];
  readonly todo: readonly TodoItem[];
}

export interface FirmwareInfo {
  readonly label: string;
  /** 版本是从哪来的。`unknown` = 没有任何通道能拿到它 */
  readonly source: 'statustext' | 'unknown';
}

export const FIRMWARE_UNKNOWN_LABEL = '未获取到';

/**
 * 从机体的状态文本里找固件版本横幅。
 *
 * ArduPilot 开机时会播报一行 `ArduCopter V4.7.1 (7f9c1a2b)`，这是**当前唯一**
 * 能拿到固件版本的通道：`AUTOPILOT_VERSION` 虽然模拟机在发，但车辆状态机还没有收下它。
 * 所以这里是"有就显示、没有就说没有"，而不是编一个版本号 —— 元数据的缓存键、
 * 参数说明与机架校验将来都按版本走，错的版本比没有版本危险得多。
 */
export function firmwareInfoOf(statustext: readonly StatustextEntry[]): FirmwareInfo {
  const pattern = /Ardu(Copter|Plane|Rover|Sub|Blimp)\s+V?(\d+\.\d+(?:\.\d+)?)/i;
  for (const entry of statustext) {
    const match = pattern.exec(entry.text);
    if (match === null) continue;
    const family = match[1] ?? '';
    const version = match[2] ?? '';
    return { label: `Ardu${family} V${version}`, source: 'statustext' };
  }
  return { label: FIRMWARE_UNKNOWN_LABEL, source: 'unknown' };
}

const CLASS_LABELS: Readonly<Record<VehicleClass, string>> = {
  copter: '多旋翼',
  plane: '固定翼',
  rover: '无人车',
  sub: '潜航器',
  other: '其它',
  unknown: '机型未知',
};

export function vehicleClassLabel(vehicleClass: VehicleClass): string {
  return CLASS_LABELS[vehicleClass];
}

export function buildVehicleFacts(input: VehicleFactsInput): VehicleFacts {
  const health: HealthItem[] = [
    {
      id: 'ekf',
      label: '姿态与位置估计（EKF）',
      level: input.ekfHealthy === null ? 'neutral' : input.ekfHealthy ? 'ok' : 'critical',
      value:
        input.ekfHealthy === null ? '没有数据' : input.ekfHealthy ? '健康' : '不健康（估计器已降级）',
      detail:
        input.ekfHealthy === null
          ? '还没有收到 EKF_STATUS_REPORT。没有它就不能说"估计器正常"。'
          : '来自 EKF_STATUS_REPORT 的状态位。不健康时不要解锁。',
      known: input.ekfHealthy !== null,
    },
    {
      id: 'gps',
      label: 'GPS / 定位',
      level:
        input.fixType === null
          ? 'neutral'
          : input.fixType >= 3
            ? 'ok'
            : input.fixType >= 2
              ? 'caution'
              : 'critical',
      value:
        input.fixType === null
          ? '没有数据'
          : `${input.fixTypeName ?? `fix_type=${input.fixType}`}${
              input.satellites === null ? '' : ` · ${input.satellites} 颗星`
            }${input.hdop === null ? '' : ` · HDOP ${input.hdop.toFixed(2)}`}`,
      detail:
        input.fixType === null
          ? '还没有收到 GPS_RAW_INT。'
          : '来自 GPS_RAW_INT 的定位类型与卫星数；3D 定位（fix_type ≥ 3）是自动模式的前提。',
      known: input.fixType !== null,
    },
    {
      id: 'battery',
      label: '电池监控',
      level:
        input.voltageV === null
          ? 'neutral'
          : input.remainingPct === null
            ? 'caution'
            : input.remainingPct <= 20
              ? 'warning'
              : 'ok',
      value:
        input.voltageV === null
          ? '没有数据'
          : `${input.voltageV.toFixed(2)} V${
              input.remainingPct === null ? ' · 电量未知' : ` · 剩余 ${input.remainingPct} %`
            }`,
      detail:
        input.voltageV === null
          ? '还没有收到 BATTERY_STATUS。'
          : '剩余电量由飞控按 BATT_CAPACITY 估算；"电量未知"通常说明容量还没设对。',
      known: input.voltageV !== null,
    },
    {
      id: 'vibration',
      label: '振动（加速度计裁剪次数）',
      level:
        input.vibrationClipping === null
          ? 'neutral'
          : input.vibrationClipping > 0
            ? 'caution'
            : 'ok',
      value:
        input.vibrationClipping === null ? '没有数据' : `累计裁剪 ${input.vibrationClipping} 次`,
      detail:
        input.vibrationClipping === null
          ? '还没有收到 VIBRATION。'
          : '裁剪次数只增不减；持续增长说明桨/机架振动已经影响到加速度计量程。',
      known: input.vibrationClipping !== null,
    },
    {
      id: 'rc',
      label: '遥控输入',
      level: input.rcChannels === null ? 'neutral' : 'ok',
      value:
        input.rcChannels === null ? '没有数据' : `已收到 ${input.rcChannels.length} 个通道`,
      detail:
        input.rcChannels === null
          ? '还没有收到 RC_CHANNELS。'
          : '通道读数只说明"收得到遥控信号"，不代表行程已经校准。',
      known: input.rcChannels !== null,
    },
    {
      id: 'compass',
      label: '罗盘',
      level: 'neutral',
      value: '无法判断',
      detail:
        '车辆状态里没有罗盘健康位（磁力计状态在 @hgc/core 的 calibration 模块，尚未接进界面）。' +
        '这里如实说"无法判断"，而不是显示一个绿色对勾。',
      known: false,
    },
  ];

  const todo: TodoItem[] = [];

  if (input.paramCount === 0) {
    todo.push({
      id: 'params-empty',
      label: '参数表还没读到',
      level: 'warning',
      detail: '没有参数表就不知道这台机体当前配置成什么样。到参数页发起一次全量读取。',
      target: 'params',
      targetLabel: '去参数编辑器',
    });
  } else if (input.downloadStatus === 'partial') {
    todo.push({
      id: 'params-partial',
      label: `参数表不完整：缺 ${input.missingParams} 条`,
      level: 'warning',
      detail: '有缺口时"没看到某个参数"不等于"机体没有它"。先在参数页重新读取补齐。',
      target: 'params',
      targetLabel: '去参数编辑器',
    });
  }

  if (input.metadataStatus === 'empty') {
    todo.push({
      id: 'metadata-empty',
      label: '参数元数据没加载（没有中文说明与取值范围）',
      level: 'info',
      detail: '参数照常读写，但看不到说明与建议范围。联网后可在参数页重试获取。',
      target: 'params',
      targetLabel: '去参数编辑器',
    });
  }

  if (input.paramCount > 0 && (input.frameClass === null || input.frameType === null)) {
    todo.push({
      id: 'frame-unknown',
      label: '机架类型未知',
      level: 'caution',
      detail: '参数表里没有 FRAME_CLASS / FRAME_TYPE，或还没读到它们。机架类型决定电机与舵机的输出顺序。',
      target: 'airframe',
      targetLabel: '去机架类型',
    });
  }

  todo.push({
    id: 'calibration',
    label: '传感器校准状态未知（本机没有校准记录）',
    level: 'caution',
    detail:
      '界面没有存储任何校准记录，也没有从遥测判断"何时校准过"的通道，' +
      '所以这里不能给出"最后一次校准时间"。装机/换件之后请按厂商流程重新校准。',
    target: 'calibration',
    targetLabel: '去传感器校准',
  });

  if (input.paramCount > 0 && input.rtlAltCm !== null && input.rtlAltCm <= 0) {
    todo.push({
      id: 'rtl-alt',
      label: '返航高度（RTL_ALT）为 0',
      level: 'caution',
      detail:
        'RTL_ALT = 0 在 ArduPilot 里的含义由固件版本决定（是"当前高度返航"还是"用默认值"），' +
        '不同版本不一致。返航是失控保护的最后一道，请显式设成你要的高度（单位 cm）。',
      target: 'safety',
      targetLabel: '去安全设置',
    });
  }

  if (input.ekfHealthy === false) {
    todo.push({
      id: 'ekf-unhealthy',
      label: '估计器（EKF）不健康',
      level: 'critical',
      detail: '位置与姿态估计处于降级状态，解锁前必须查清原因（振动、罗盘、GPS 都可能引起）。',
      target: 'summary',
      targetLabel: null,
    });
  }

  if (input.fixType !== null && input.fixType < 3) {
    todo.push({
      id: 'gps-no-fix',
      label: 'GPS 没有 3D 定位',
      level: 'warning',
      detail: '自动模式（自动/返航/引导）依赖 3D 定位。请在开阔处等待定位，或检查天线与配置。',
      target: 'summary',
      targetLabel: null,
    });
  }

  if (input.rcChannels === null) {
    todo.push({
      id: 'rc-missing',
      label: '还没收到遥控通道数据',
      level: 'caution',
      detail: '没有通道读数就无法校准行程，也无法确认遥控链路是否可用。',
      target: 'radio',
      targetLabel: '去遥控器',
    });
  }

  if (input.voltageV !== null && input.remainingPct === null) {
    todo.push({
      id: 'battery-capacity',
      label: '剩余电量未知（电池容量可能没设）',
      level: 'info',
      detail: '电压能读到，但飞控没有给出剩余电量。检查 BATT_CAPACITY 与电池监控的标定。',
      target: 'power',
      targetLabel: '去电源与电机',
    });
  }

  if (input.vibrationClipping !== null && input.vibrationClipping > 0) {
    todo.push({
      id: 'vibration',
      label: `振动裁剪已累计 ${input.vibrationClipping} 次`,
      level: 'caution',
      detail: '持续增长会污染姿态估计。建议做一次振动与频谱分析（调参页尚未接入）。',
      target: 'tuning',
      targetLabel: '去调参',
    });
  }

  return { health, todo };
}
